import { useState } from "react"

export interface DataPoint  { label: string; value: number }
export interface GroupPoint { label: string; a: number; b: number }
export interface PieSlice   { label: string; value: number; color: string }

// ── math helpers ──────────────────────────────────────────────────────────────

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function donutPath(cx: number, cy: number, ro: number, ri: number, a0: number, a1: number) {
  const clamp = Math.min(a1, a0 + 359.99)
  const large = clamp - a0 > 180 ? 1 : 0
  const os = polar(cx, cy, ro, a0)
  const oe = polar(cx, cy, ro, clamp)
  const is = polar(cx, cy, ri, a0)
  const ie = polar(cx, cy, ri, clamp)
  return [
    `M ${f(os.x)} ${f(os.y)}`,
    `A ${ro} ${ro} 0 ${large} 1 ${f(oe.x)} ${f(oe.y)}`,
    `L ${f(ie.x)} ${f(ie.y)}`,
    `A ${ri} ${ri} 0 ${large} 0 ${f(is.x)} ${f(is.y)}`,
    "Z",
  ].join(" ")
}

function f(n: number) { return n.toFixed(2) }

function niceMax(v: number) {
  if (v <= 0) return 10
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  return Math.ceil(v / mag) * mag
}

// ── ChartCard ─────────────────────────────────────────────────────────────────

export const ChartCard = ({
  title, children, note,
}: { title: string; children: React.ReactNode; note?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
    {children}
    {note && <p className="text-[11px] text-gray-300 mt-2 text-right">{note}</p>}
  </div>
)

// ── Tooltip bubble ────────────────────────────────────────────────────────────

function Tip({ x, y, top, bottom, lines, W }: {
  x: number; y: number; top: number; bottom: number; lines: string[]; W: number
}) {
  const bw = 96, bh = lines.length * 14 + 14
  const tx = Math.max(4, Math.min(x - bw / 2, W - bw - 4))
  const ty = Math.max(top, y - bh - 10)
  return (
    <g>
      <rect x={tx} y={ty} width={bw} height={bh} rx={7}
        fill="white" stroke="#e5e7eb" strokeWidth={1}
        style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.08))" }} />
      {lines.map((l, i) => (
        <text key={i} x={tx + bw / 2} y={ty + 13 + i * 14}
          textAnchor="middle" fontSize={i === 0 ? 9 : 11}
          fill={i === 0 ? "#9ca3af" : "#111827"}
          fontWeight={i === 0 ? "400" : "700"}>
          {l}
        </text>
      ))}
    </g>
  )
}

// ── LineChart ─────────────────────────────────────────────────────────────────

const LW = 580, LH = 165
const LP = { t: 12, b: 28, l: 46, r: 14 }
const liw = LW - LP.l - LP.r
const lih = LH - LP.t - LP.b

export const LineChart = ({
  data, color = "#3b82f6",
}: { data: DataPoint[]; color?: string }) => {
  const [hov, setHov] = useState<number | null>(null)
  if (!data.length) return <Empty />

  const max = niceMax(Math.max(...data.map((d) => d.value)))
  const sx = (i: number) => LP.l + (i / Math.max(data.length - 1, 1)) * liw
  const sy = (v: number) => LP.t + (1 - v / max) * lih

  const pts = data.map((d, i) => `${f(sx(i))},${f(sy(d.value))}`).join(" ")
  const area = `${f(sx(0))},${f(LH - LP.b)} ${pts} ${f(sx(data.length - 1))},${f(LH - LP.b)}`
  const yTicks = [0, 0.5, 1].map((frac) => ({ v: Math.round(max * frac), y: LP.t + (1 - frac) * lih }))
  const step = Math.max(1, Math.floor(data.length / 6))
  const xIdx = data.map((_, i) => i).filter((i) => i % step === 0 || i === data.length - 1)
  const gid = `lg_${color.replace("#", "")}`

  return (
    <svg viewBox={`0 0 ${LW} ${LH}`} className="w-full" style={{ height: LH }}
      onMouseLeave={() => setHov(null)}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>
      {yTicks.map((t) => (
        <g key={t.y}>
          <line x1={LP.l} y1={t.y} x2={LW - LP.r} y2={t.y} stroke="#f3f4f6" strokeWidth={1} />
          <text x={LP.l - 6} y={t.y + 4} textAnchor="end" fontSize={9} fill="#9ca3af">
            {t.v >= 1000 ? `${(t.v / 1000).toFixed(0)}K` : t.v}
          </text>
        </g>
      ))}
      {xIdx.map((i) => (
        <text key={i} x={sx(i)} y={LH - 6} textAnchor="middle" fontSize={9} fill="#9ca3af">
          {data[i].label.slice(-5)}
        </text>
      ))}
      <polygon points={area} fill={`url(#${gid})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2.2}
        strokeLinejoin="round" strokeLinecap="round" />
      {/* invisible hit targets */}
      {data.map((_, i) => (
        <rect key={i} x={sx(i) - 8} y={LP.t} width={16} height={lih}
          fill="transparent" onMouseEnter={() => setHov(i)} />
      ))}
      {hov !== null && (
        <>
          <line x1={sx(hov)} y1={LP.t} x2={sx(hov)} y2={LH - LP.b}
            stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />
          <circle cx={sx(hov)} cy={sy(data[hov].value)} r={4.5}
            fill={color} stroke="white" strokeWidth={2} />
          <Tip x={sx(hov)} y={sy(data[hov].value)} top={LP.t} bottom={LH - LP.b}
            lines={[data[hov].label, data[hov].value.toLocaleString("ru")]} W={LW} />
        </>
      )}
    </svg>
  )
}

// ── VBarChart ─────────────────────────────────────────────────────────────────

const VW = 580, VH = 165
const VP = { t: 12, b: 28, l: 46, r: 14 }
const viw = VW - VP.l - VP.r
const vih = VH - VP.t - VP.b

export const VBarChart = ({
  data, color = "#3b82f6",
}: { data: DataPoint[]; color?: string }) => {
  const [hov, setHov] = useState<number | null>(null)
  if (!data.length) return <Empty />

  const max = niceMax(Math.max(...data.map((d) => d.value)))
  const slot = viw / data.length
  const bw = Math.max(8, slot * 0.55)
  const bx = (i: number) => VP.l + i * slot + slot / 2 - bw / 2
  const bh = (v: number) => Math.max(2, (v / max) * vih)
  const yTicks = [0, 0.5, 1].map((frac) => ({ v: Math.round(max * frac), y: VP.t + (1 - frac) * vih }))

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ height: VH }}
      onMouseLeave={() => setHov(null)}>
      {yTicks.map((t) => (
        <g key={t.y}>
          <line x1={VP.l} y1={t.y} x2={VW - VP.r} y2={t.y} stroke="#f3f4f6" strokeWidth={1} />
          <text x={VP.l - 6} y={t.y + 4} textAnchor="end" fontSize={9} fill="#9ca3af">
            {t.v >= 1000 ? `${(t.v / 1000).toFixed(0)}K` : t.v}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const h = bh(d.value)
        const x = bx(i)
        const y = VH - VP.b - h
        return (
          <g key={i} onMouseEnter={() => setHov(i)} style={{ cursor: "default" }}>
            <rect x={x} y={y} width={bw} height={h} rx={4}
              fill={color} opacity={hov === i ? 1 : 0.75} />
            <rect x={VP.l + i * slot} y={VP.t} width={slot} height={vih} fill="transparent" />
            <text x={x + bw / 2} y={VH - 6} textAnchor="middle" fontSize={9} fill="#9ca3af">
              {d.label.length > 8 ? d.label.slice(0, 7) + "…" : d.label}
            </text>
            {hov === i && (
              <Tip x={x + bw / 2} y={y} top={VP.t} bottom={VH - VP.b}
                lines={[d.label, d.value.toLocaleString("ru")]} W={VW} />
            )}
          </g>
        )
      })}
    </svg>
  )
}

// ── HBarChart ─────────────────────────────────────────────────────────────────

export const HBarChart = ({
  data, color = "#3b82f6",
}: { data: DataPoint[]; color?: string }) => {
  const [hov, setHov] = useState<number | null>(null)
  if (!data.length) return <Empty />

  const max = Math.max(...data.map((d) => d.value))
  const labelW = 110, barMaxW = 300, numW = 70, padT = 4
  const rowH = 30
  const TW = labelW + barMaxW + numW
  const TH = data.length * rowH + padT * 2

  return (
    <svg viewBox={`0 0 ${TW} ${TH}`} className="w-full"
      style={{ height: Math.max(TH, 60) }}
      onMouseLeave={() => setHov(null)}>
      {data.map((d, i) => {
        const bw = Math.max(2, (d.value / max) * barMaxW)
        const y = padT + i * rowH
        const mid = y + rowH / 2
        return (
          <g key={i} onMouseEnter={() => setHov(i)} style={{ cursor: "default" }}>
            {/* label */}
            <text x={labelW - 8} y={mid + 4} textAnchor="end" fontSize={11} fill="#374151">
              {d.label.length > 14 ? d.label.slice(0, 13) + "…" : d.label}
            </text>
            {/* track */}
            <rect x={labelW} y={mid - 6} width={barMaxW} height={12} rx={4} fill="#f3f4f6" />
            {/* bar */}
            <rect x={labelW} y={mid - 6} width={bw} height={12} rx={4}
              fill={color} opacity={hov === i ? 1 : 0.8} />
            {/* value */}
            <text x={labelW + barMaxW + 8} y={mid + 4} fontSize={11} fontWeight="600" fill="#111827">
              {d.value.toLocaleString("ru")}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ── GroupedBarChart ───────────────────────────────────────────────────────────

const GW = 580, GH = 180
const GP = { t: 12, b: 42, l: 46, r: 14 }
const giw = GW - GP.l - GP.r
const gih = GH - GP.t - GP.b

export const GroupedBarChart = ({
  data, labelA, labelB,
  colorA = "#3b82f6", colorB = "#10b981",
}: {
  data: GroupPoint[]; labelA: string; labelB: string
  colorA?: string; colorB?: string
}) => {
  const [hov, setHov] = useState<number | null>(null)
  if (!data.length) return <Empty />

  const max = niceMax(Math.max(...data.flatMap((d) => [d.a, d.b])))
  const slot = giw / data.length
  const bw = Math.max(6, slot * 0.28)
  const gap = Math.max(2, bw * 0.2)
  const bx = (i: number, j: 0 | 1) =>
    GP.l + i * slot + slot / 2 - bw - gap / 2 + j * (bw + gap)
  const bh = (v: number) => Math.max(2, (v / max) * gih)
  const yTicks = [0, 0.5, 1].map((frac) => ({ v: Math.round(max * frac), y: GP.t + (1 - frac) * gih }))

  return (
    <div>
      <svg viewBox={`0 0 ${GW} ${GH}`} className="w-full" style={{ height: GH }}
        onMouseLeave={() => setHov(null)}>
        {yTicks.map((t) => (
          <g key={t.y}>
            <line x1={GP.l} y1={t.y} x2={GW - GP.r} y2={t.y} stroke="#f3f4f6" strokeWidth={1} />
            <text x={GP.l - 6} y={t.y + 4} textAnchor="end" fontSize={9} fill="#9ca3af">
              {t.v >= 1000 ? `${(t.v / 1000).toFixed(0)}K` : t.v}
            </text>
          </g>
        ))}
        {data.map((d, i) => {
          const ha = bh(d.a), hb = bh(d.b)
          const midX = GP.l + i * slot + slot / 2
          const labelText = d.label.length > 10 ? d.label.slice(0, 9) + "…" : d.label
          return (
            <g key={i} onMouseEnter={() => setHov(i)} style={{ cursor: "default" }}>
              <rect x={bx(i, 0)} y={GH - GP.b - ha} width={bw} height={ha} rx={3}
                fill={colorA} opacity={hov === i ? 1 : 0.78} />
              <rect x={bx(i, 1)} y={GH - GP.b - hb} width={bw} height={hb} rx={3}
                fill={colorB} opacity={hov === i ? 1 : 0.78} />
              {/* hit target */}
              <rect x={GP.l + i * slot} y={GP.t} width={slot} height={gih} fill="transparent" />
              <text x={midX} y={GH - GP.b + 13} textAnchor="middle" fontSize={9} fill="#9ca3af">
                {labelText}
              </text>
              {hov === i && (
                <Tip
                  x={midX} y={GH - GP.b - Math.max(ha, hb)}
                  top={GP.t} bottom={GH - GP.b}
                  lines={[d.label, `${labelA}: ${d.a.toLocaleString("ru")}`, `${labelB}: ${d.b.toLocaleString("ru")}`]}
                  W={GW}
                />
              )}
            </g>
          )
        })}
      </svg>
      <div className="flex items-center justify-center gap-5 -mt-1">
        <Dot color={colorA} label={labelA} />
        <Dot color={colorB} label={labelB} />
      </div>
    </div>
  )
}

// ── DonutChart ────────────────────────────────────────────────────────────────

export const DonutChart = ({ data }: { data: PieSlice[] }) => {
  const [hov, setHov] = useState<number | null>(null)
  if (!data.length) return <Empty />

  const total = data.reduce((s, d) => s + d.value, 0)
  const cx = 90, cy = 90, ro = 70, ri = 42

  let angle = -90
  const slices = data.map((d) => {
    const sweep = (d.value / total) * 360
    const a0 = angle
    const a1 = angle + sweep
    angle += sweep
    return { ...d, a0, a1 }
  })

  const active = hov !== null ? slices[hov] : null

  return (
    <div className="flex items-center gap-5 flex-wrap">
      <svg viewBox="0 0 180 180" style={{ width: 180, height: 180, flexShrink: 0 }}>
        {slices.map((s, i) => (
          <path key={i}
            d={donutPath(cx, cy, ro, ri, s.a0, s.a1)}
            fill={s.color}
            opacity={hov === null || hov === i ? 1 : 0.45}
            style={{ cursor: "pointer", transition: "opacity 0.15s" }}
            onMouseEnter={() => setHov(i)}
            onMouseLeave={() => setHov(null)}
          />
        ))}
        {/* center */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={18} fontWeight="700" fill="#111827">
          {active
            ? `${((active.value / total) * 100).toFixed(0)}%`
            : total.toLocaleString("ru")}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize={10} fill="#9ca3af">
          {active ? active.label.slice(0, 12) : "всего"}
        </text>
      </svg>
      <div className="flex flex-col gap-2.5 min-w-0">
        {data.map((d, i) => (
          <div key={i}
            className="flex items-center gap-2 cursor-default min-w-0"
            onMouseEnter={() => setHov(i)}
            onMouseLeave={() => setHov(null)}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ background: d.color }} />
            <span className="text-xs text-gray-600 truncate">{d.label}</span>
            <span className="text-xs font-bold text-gray-900 ml-auto pl-3 flex-shrink-0">
              {d.value.toLocaleString("ru")}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

const Empty = ({ msg = "Нет данных" }: { msg?: string }) => (
  <div className="flex items-center justify-center" style={{ height: 120 }}>
    <p className="text-sm text-gray-300">{msg}</p>
  </div>
)

const Dot = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-1.5">
    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
    <span className="text-xs text-gray-500">{label}</span>
  </div>
)
